import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export interface UpcomingPaymentSupplier {
  account: string;
  name: string;
  country: string | null;
}

export interface UpcomingPaymentStage {
  number: number;
  code: string;
}

export interface UpcomingPaymentInvoice {
  invoice: string;
  invoiceDate: string | null;
  supplier: UpcomingPaymentSupplier;
  stage: UpcomingPaymentStage;
  expectedDeliveryDate: string | null;
  expectedDeliverySource: string | null;
  currency: string;
  invoiceValue: number;
  invoiceValueGbp: number | null;
  purchaseOrders: Array<{
    poNumber?: string | null;
    termsCode?: string | null;
    orderValue?: number | null;
    outstandingOrderValue?: number | null;
    baseOrderValueGbp?: number | null;
    outstandingBaseOrderValueGbp?: number | null;
    shippedValue?: number | null;
    currency?: string | null;
    [key: string]: unknown;
  }>;
  paymentKey: string;
  termsCodes: string[];
  termsBusinessDays: number | null;
  paymentDate: string | null;
  paymentDateIssue: string | null;
}

export interface UpcomingPaymentNote {
  id: number;
  paymentKey: string;
  body: string;
  createdAt: string;
  createdBy: number;
  createdByName: string | null;
}

export interface UpcomingPaymentTerms {
  termsCode: string;
  businessDays: number | null;
}

export interface UpcomingPaymentsResponse {
  generatedAt: string;
  invoices: UpcomingPaymentInvoice[];
  terms: UpcomingPaymentTerms[];
  notes: UpcomingPaymentNote[];
}

export function useUpcomingPayments() {
  return useQuery<UpcomingPaymentsResponse>({
    queryKey: ["upcoming-payments"],
    queryFn: async () => {
      let res: Response;
      try {
        res = await fetch(`${BASE}/api/upcoming-payments`, {
          credentials: "include",
          signal: AbortSignal.timeout(20_000),
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === "TimeoutError") {
          throw new Error("Upcoming payments took too long to respond. Please try again.");
        }
        throw error;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Failed to fetch upcoming payments");
      if (!Array.isArray(body?.invoices) || !Array.isArray(body?.terms) || !Array.isArray(body?.notes)) {
        throw new Error("Upcoming payments returned an invalid response");
      }
      return body;
    },
    retry: 1,
  });
}

export function useUpdateTerms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ termsCode, businessDays }: { termsCode: string; businessDays: number | null }) => {
      const res = await fetch(`${BASE}/api/upcoming-payments/terms/${encodeURIComponent(termsCode)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessDays }),
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Failed to update terms");
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["upcoming-payments"] });
    },
  });
}

export function useCreateNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ paymentKey, body }: { paymentKey: string; body: string }) => {
      const res = await fetch(`${BASE}/api/upcoming-payments/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentKey, body }),
        credentials: "include",
      });
      const responseBody = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(responseBody?.error || "Failed to add note");
      return responseBody;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["upcoming-payments"] });
    },
  });
}

